import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks for each report
export const fetchQuickBooksRevenueReport = createAsyncThunk(
  'quickbooksReports/fetchRevenue',
  async ({ forceRefresh = false, startDate, endDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/revenue',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksStaffingReport = createAsyncThunk(
  'quickbooksReports/fetchStaffing',
  async ({ forceRefresh = false, startDate, endDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/staffing',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksDirectExpensesReport = createAsyncThunk(
  'quickbooksReports/fetchDirectExpenses',
  async ({ forceRefresh = false, startDate, endDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/direct-expenses',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksIndirectExpensesReport = createAsyncThunk(
  'quickbooksReports/fetchIndirectExpenses',
  async ({ forceRefresh = false, startDate, endDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/indirect-expenses',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksAssetsReport = createAsyncThunk(
  'quickbooksReports/fetchAssets',
  async ({ forceRefresh = false, asOfDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (asOfDate) params.as_of_date = asOfDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/assets',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksFundingReport = createAsyncThunk(
  'quickbooksReports/fetchFunding',
  async ({ forceRefresh = false, asOfDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (asOfDate) params.as_of_date = asOfDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/funding',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksProfitLossReport = createAsyncThunk(
  'quickbooksReports/fetchProfitLoss',
  async ({ forceRefresh = false, startDate, endDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/profit-loss',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksCashFlowReport = createAsyncThunk(
  'quickbooksReports/fetchCashFlow',
  async ({ forceRefresh = false, startDate, endDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/cash-flow',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksBalanceSheetReport = createAsyncThunk(
  'quickbooksReports/fetchBalanceSheet',
  async ({ forceRefresh = false, asOfDate } = {}, { rejectWithValue, getState }) => {
    try {
      const params = {};
      if (forceRefresh) params.force_refresh = true;
      if (asOfDate) params.as_of_date = asOfDate;

      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/reports/balance-sheet',
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuickBooksStatus = createAsyncThunk(
  'quickbooksReports/fetchStatus',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.Token;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        '/financials/integrations/quickbooks/status',
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialReportState = {
  data: null,
  loading: false,
  error: null,
  syncedAt: null,
  fromCache: false,
};

const quickbooksReportsSlice = createSlice({
  name: 'quickbooksReports',
  initialState: {
    revenue: { ...initialReportState },
    staffing: { ...initialReportState },
    directExpenses: { ...initialReportState },
    indirectExpenses: { ...initialReportState },
    assets: { ...initialReportState },
    funding: { ...initialReportState },
    profitLoss: { ...initialReportState },
    cashFlow: { ...initialReportState },
    balanceSheet: { ...initialReportState },
    connectionStatus: {
      connected: false,
      loading: false,
      error: null,
    },
  },
  reducers: {
    clearReportData: (state, action) => {
      const reportType = action.payload;
      if (state[reportType]) {
        state[reportType] = { ...initialReportState };
      }
    },
    clearAllReports: (state) => {
      state.revenue = { ...initialReportState };
      state.staffing = { ...initialReportState };
      state.directExpenses = { ...initialReportState };
      state.indirectExpenses = { ...initialReportState };
      state.assets = { ...initialReportState };
      state.funding = { ...initialReportState };
      state.profitLoss = { ...initialReportState };
      state.cashFlow = { ...initialReportState };
      state.balanceSheet = { ...initialReportState };
    },
  },
  extraReducers: (builder) => {
    // Revenue Report
    builder
      .addCase(fetchQuickBooksRevenueReport.pending, (state) => {
        state.revenue.loading = true;
        state.revenue.error = null;
      })
      .addCase(fetchQuickBooksRevenueReport.fulfilled, (state, action) => {
        state.revenue.loading = false;
        state.revenue.data = action.payload.data;
        state.revenue.syncedAt = action.payload.synced_at;
        state.revenue.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksRevenueReport.rejected, (state, action) => {
        state.revenue.loading = false;
        state.revenue.error = action.payload || 'Failed to fetch revenue report';
      });

    // Staffing Report
    builder
      .addCase(fetchQuickBooksStaffingReport.pending, (state) => {
        state.staffing.loading = true;
        state.staffing.error = null;
      })
      .addCase(fetchQuickBooksStaffingReport.fulfilled, (state, action) => {
        state.staffing.loading = false;
        state.staffing.data = action.payload.data;
        state.staffing.syncedAt = action.payload.synced_at;
        state.staffing.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksStaffingReport.rejected, (state, action) => {
        state.staffing.loading = false;
        state.staffing.error = action.payload || 'Failed to fetch staffing report';
      });

    // Direct Expenses Report
    builder
      .addCase(fetchQuickBooksDirectExpensesReport.pending, (state) => {
        state.directExpenses.loading = true;
        state.directExpenses.error = null;
      })
      .addCase(fetchQuickBooksDirectExpensesReport.fulfilled, (state, action) => {
        state.directExpenses.loading = false;
        state.directExpenses.data = action.payload.data;
        state.directExpenses.syncedAt = action.payload.synced_at;
        state.directExpenses.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksDirectExpensesReport.rejected, (state, action) => {
        state.directExpenses.loading = false;
        state.directExpenses.error = action.payload || 'Failed to fetch direct expenses report';
      });

    // Indirect Expenses Report
    builder
      .addCase(fetchQuickBooksIndirectExpensesReport.pending, (state) => {
        state.indirectExpenses.loading = true;
        state.indirectExpenses.error = null;
      })
      .addCase(fetchQuickBooksIndirectExpensesReport.fulfilled, (state, action) => {
        state.indirectExpenses.loading = false;
        state.indirectExpenses.data = action.payload.data;
        state.indirectExpenses.syncedAt = action.payload.synced_at;
        state.indirectExpenses.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksIndirectExpensesReport.rejected, (state, action) => {
        state.indirectExpenses.loading = false;
        state.indirectExpenses.error = action.payload || 'Failed to fetch indirect expenses report';
      });

    // Assets Report
    builder
      .addCase(fetchQuickBooksAssetsReport.pending, (state) => {
        state.assets.loading = true;
        state.assets.error = null;
      })
      .addCase(fetchQuickBooksAssetsReport.fulfilled, (state, action) => {
        state.assets.loading = false;
        state.assets.data = action.payload.data;
        state.assets.syncedAt = action.payload.synced_at;
        state.assets.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksAssetsReport.rejected, (state, action) => {
        state.assets.loading = false;
        state.assets.error = action.payload || 'Failed to fetch assets report';
      });

    // Funding Report
    builder
      .addCase(fetchQuickBooksFundingReport.pending, (state) => {
        state.funding.loading = true;
        state.funding.error = null;
      })
      .addCase(fetchQuickBooksFundingReport.fulfilled, (state, action) => {
        state.funding.loading = false;
        state.funding.data = action.payload.data;
        state.funding.syncedAt = action.payload.synced_at;
        state.funding.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksFundingReport.rejected, (state, action) => {
        state.funding.loading = false;
        state.funding.error = action.payload || 'Failed to fetch funding report';
      });

    // Profit & Loss Report
    builder
      .addCase(fetchQuickBooksProfitLossReport.pending, (state) => {
        state.profitLoss.loading = true;
        state.profitLoss.error = null;
      })
      .addCase(fetchQuickBooksProfitLossReport.fulfilled, (state, action) => {
        state.profitLoss.loading = false;
        state.profitLoss.data = action.payload.data;
        state.profitLoss.syncedAt = action.payload.synced_at;
        state.profitLoss.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksProfitLossReport.rejected, (state, action) => {
        state.profitLoss.loading = false;
        state.profitLoss.error = action.payload || 'Failed to fetch Profit & Loss report';
      });

    // Cash Flow Report
    builder
      .addCase(fetchQuickBooksCashFlowReport.pending, (state) => {
        state.cashFlow.loading = true;
        state.cashFlow.error = null;
      })
      .addCase(fetchQuickBooksCashFlowReport.fulfilled, (state, action) => {
        state.cashFlow.loading = false;
        state.cashFlow.data = action.payload.data;
        state.cashFlow.syncedAt = action.payload.synced_at;
        state.cashFlow.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksCashFlowReport.rejected, (state, action) => {
        state.cashFlow.loading = false;
        state.cashFlow.error = action.payload || 'Failed to fetch Cash Flow report';
      });

    // Balance Sheet Report
    builder
      .addCase(fetchQuickBooksBalanceSheetReport.pending, (state) => {
        state.balanceSheet.loading = true;
        state.balanceSheet.error = null;
      })
      .addCase(fetchQuickBooksBalanceSheetReport.fulfilled, (state, action) => {
        state.balanceSheet.loading = false;
        state.balanceSheet.data = action.payload.data;
        state.balanceSheet.syncedAt = action.payload.synced_at;
        state.balanceSheet.fromCache = action.payload.from_cache;
      })
      .addCase(fetchQuickBooksBalanceSheetReport.rejected, (state, action) => {
        state.balanceSheet.loading = false;
        state.balanceSheet.error = action.payload || 'Failed to fetch Balance Sheet report';
      });

    // Connection Status
    builder
      .addCase(fetchQuickBooksStatus.pending, (state) => {
        state.connectionStatus.loading = true;
        state.connectionStatus.error = null;
      })
      .addCase(fetchQuickBooksStatus.fulfilled, (state, action) => {
        state.connectionStatus.loading = false;
        state.connectionStatus.connected = action.payload.connected;
      })
      .addCase(fetchQuickBooksStatus.rejected, (state, action) => {
        state.connectionStatus.loading = false;
        state.connectionStatus.error = action.payload || 'Failed to fetch connection status';
      });
  },
});

export const { clearReportData, clearAllReports } = quickbooksReportsSlice.actions;
export default quickbooksReportsSlice.reducer;

